import {
  REALTIME_EVENTS,
  type AlertCreatedEvent,
  type AlertUpdatedEvent,
  type ApplianceStatusChangedEvent,
} from '@minisoc/contracts';
import { io, type Socket } from 'socket.io-client';

export interface RealtimeHandlers {
  onAlertCreated?: (event: AlertCreatedEvent) => void;
  onAlertUpdated?: (event: AlertUpdatedEvent) => void;
  onApplianceStatusChanged?: (event: ApplianceStatusChangedEvent) => void;
  onConnectionChange?: (connected: boolean) => void;
}

/**
 * Connexion temps réel aux alertes.
 *
 * Le token est fourni par une fonction et non par une valeur : il tourne toutes
 * les 15 minutes, et socket.io le relit à chaque tentative de reconnexion. Passer
 * la valeur figée conduirait à une reconnexion refusée après la première
 * expiration.
 */
export function connectRealtime(
  wsUrl: string,
  getToken: () => string | null,
  handlers: RealtimeHandlers,
): { disconnect: () => void } {
  const socket: Socket = io(`${wsUrl}/realtime`, {
    transports: ['websocket'],
    auth: (callback) => callback({ token: getToken() ?? '' }),
    reconnection: true,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 15_000,
  });

  socket.on('connect', () => handlers.onConnectionChange?.(true));
  socket.on('disconnect', () => handlers.onConnectionChange?.(false));

  // Le serveur ferme explicitement les sockets non authentifiés. Sans arrêter la
  // reconnexion, le client bouclerait sur un refus après expiration de session.
  socket.on('connection_refused', () => {
    handlers.onConnectionChange?.(false);
    socket.disconnect();
  });

  if (handlers.onAlertCreated) {
    socket.on(REALTIME_EVENTS.alertCreated, handlers.onAlertCreated);
  }
  if (handlers.onAlertUpdated) {
    socket.on(REALTIME_EVENTS.alertUpdated, handlers.onAlertUpdated);
  }
  if (handlers.onApplianceStatusChanged) {
    socket.on(REALTIME_EVENTS.applianceStatusChanged, handlers.onApplianceStatusChanged);
  }

  return { disconnect: () => socket.disconnect() };
}
